import { useMemo } from 'react';
import {
  AlertTriangle,
  Boxes,
  CalendarClock,
  ChevronRight,
  Package,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { DonutChart } from '@/components/charts/DonutChart';
import { BarChart } from '@/components/charts/BarChart';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { palette } from '@/components/charts/chartTheme';
import { useFilters, rangeISO } from '@/state/filters';
import { useInventoryHealth, usePaymentMix } from '@/api/bi';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Supply — inventory health + payments mix.
 *
 * Stage 1 polish:
 *   - Animated KPI tiles with semantic tone (danger/warning/success/neutral).
 *   - "Supply alerts" strip surfaces only the issues that actually need eyes
 *     today (open stockouts, near-expiry, damage above threshold). Quiet when
 *     things are healthy — never noise for noise's sake.
 *   - Empty states get a CTA pointing the user at the Stock Ledger.
 */
export function SupplyPage() {
  const { range, storeId, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const filters = { start: iso.start, end: iso.end, storeId };

  const inventory = useInventoryHealth(filters);
  const payments = usePaymentMix(filters);

  const inv = inventory.data;
  const mix = payments.data;

  const paymentDonut = useMemo(
    () => (mix?.mix ?? []).map((r) => ({ name: r.payment_type.toUpperCase(), value: r.orders })),
    [mix],
  );
  const paymentBar = useMemo(
    () => (mix?.mix ?? []).map((r) => ({ label: r.payment_type.toUpperCase(), gmv: r.gmv })),
    [mix],
  );

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Inventory & Payments"
        description="Stockouts, near-expiry, damage rate, and how customers are paying."
      />

      <SupplyAlertsStrip inv={inv} loading={inventory.isLoading} />

      <div className="mt-4 grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
        <SupplyStat
          label="Open stockouts"
          value={inv?.stockouts.open_now ?? null}
          format={(n) => fmt.number(n)}
          loading={inventory.isLoading}
          icon={<ShieldAlert className="h-4 w-4" />}
          tone={inv && inv.stockouts.open_now > 0 ? 'danger' : 'success'}
          hint={inv ? `${fmt.number(inv.stockouts.total_in_window)} in window` : undefined}
        />
        <SupplyStat
          label="Low-stock SKUs"
          value={inv?.availability.rows_low ?? null}
          format={(n) => fmt.number(n)}
          loading={inventory.isLoading}
          icon={<AlertTriangle className="h-4 w-4" />}
          tone="warning"
          hint={inv ? `${fmt.number(inv.availability.rows_zero)} at zero` : undefined}
        />
        <SupplyStat
          label="Near-expiry units"
          value={inv?.units.near_expiry_14d ?? null}
          format={(n) => fmt.number(n)}
          loading={inventory.isLoading}
          icon={<CalendarClock className="h-4 w-4" />}
          tone={inv && inv.units.near_expiry_14d > 0 ? 'warning' : 'neutral'}
          hint="Expires within 14 days"
        />
        <SupplyStat
          label="Damage rate"
          value={inv?.units.damage_rate ?? null}
          format={(n) => fmt.pct(n)}
          loading={inventory.isLoading}
          icon={<Package className="h-4 w-4" />}
          tone={inv && inv.units.damage_rate > 0.02 ? 'danger' : 'success'}
          hint={inv ? `${fmt.number(inv.units.damaged)} damaged units` : undefined}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Stock availability</CardTitle>
          </CardHeader>
          <CardBody>
            {inventory.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : inventory.isError ? (
              <ErrorState onRetry={() => void inventory.refetch()} />
            ) : !inv ? null : (
              <ul className="space-y-2 text-sm">
                <KVRow label="SKUs tracked" value={inv.availability.rows_total} />
                <KVRow label="Below min level" value={inv.availability.rows_low} />
                <KVRow label="At zero" value={inv.availability.rows_zero} tone="danger" />
                <KVRow label="Available units" value={inv.units.available} />
                <KVRow label="Damaged units" value={inv.units.damaged} tone="danger" />
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment mix · orders</CardTitle>
          </CardHeader>
          <CardBody>
            {payments.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : payments.isError ? (
              <ErrorState onRetry={() => void payments.refetch()} />
            ) : paymentDonut.length === 0 ? (
              <EmptyState
                icon={<Package className="h-5 w-5" />}
                title="No completed payments"
                description="No paid orders in this window."
              />
            ) : (
              <>
                <DonutChart data={paymentDonut} formatValue={(v) => fmt.number(v)} />
                <ul className="mt-2 space-y-1 text-sm">
                  {(mix?.mix ?? []).map((r, i) => (
                    <li key={r.payment_type} className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: palette.series[i % palette.series.length]() }}
                        />
                        <span className="capitalize">{r.payment_type}</span>
                      </span>
                      <span className="text-muted-foreground">
                        <span className="kpi-num font-medium text-foreground">
                          <AnimatedNumber value={r.orders} format={(n) => fmt.number(n)} duration={400} />
                        </span>{' '}
                        · {fmt.pct(r.share_orders)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment mix · GMV</CardTitle>
            {mix ? <Badge tone="neutral">{fmt.npr(mix.totals.gmv)}</Badge> : null}
          </CardHeader>
          <CardBody>
            {payments.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : paymentBar.length === 0 ? (
              <EmptyState icon={<Package className="h-5 w-5" />} title="No GMV" />
            ) : (
              <BarChart
                data={paymentBar}
                xKey="label"
                yKey="gmv"
                formatValue={(v) => fmt.npr(v)}
                colorFor={(_, i) => palette.series[i % palette.series.length]()}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

/**
 * Surfaces only the issues that need attention. Renders a single
 * "all clear" tile when nothing's wrong — small reward for healthy supply.
 */
function SupplyAlertsStrip({
  inv,
  loading,
}: {
  inv: ReturnType<typeof useInventoryHealth>['data'];
  loading: boolean;
}) {
  if (loading || !inv) return null;

  const alerts: Array<{
    label: string;
    detail: string;
    tone: 'danger' | 'warning';
    to?: string;
  }> = [];

  if (inv.stockouts.open_now > 0) {
    alerts.push({
      label: `${fmt.number(inv.stockouts.open_now)} open stockout${inv.stockouts.open_now === 1 ? '' : 's'}`,
      detail: 'SKUs currently at zero across selected stores.',
      tone: 'danger',
      to: '/inventory',
    });
  }
  if (inv.availability.rows_zero > 0 && inv.availability.rows_zero !== inv.stockouts.open_now) {
    alerts.push({
      label: `${fmt.number(inv.availability.rows_zero)} SKUs at zero`,
      detail: 'Available units fell to zero — replenish or hide.',
      tone: 'warning',
      to: '/inventory',
    });
  }
  if (inv.units.near_expiry_14d > 0) {
    alerts.push({
      label: `${fmt.number(inv.units.near_expiry_14d)} units expiring soon`,
      detail: 'Will pass best-before within 14 days.',
      tone: 'warning',
    });
  }
  if (inv.units.damage_rate > 0.02) {
    alerts.push({
      label: `Damage rate ${fmt.pct(inv.units.damage_rate)}`,
      detail: 'Damaged ÷ total available is above the 2% target.',
      tone: 'danger',
      to: '/inventory',
    });
  }

  if (alerts.length === 0) {
    return (
      <Card className="bg-dotgrid">
        <CardBody className="!py-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-success/10 text-success">
              <ShieldCheck className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <p className="font-medium">Supply looks healthy</p>
              <p className="text-xs text-muted-foreground">
                No open stockouts, near-expiry units or damage spikes for this window.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {alerts.map((a, i) => {
        const Icon = a.tone === 'danger' ? ShieldAlert : AlertTriangle;
        const Wrapper: React.ElementType = a.to ? Link : 'div';
        const linkProps = a.to ? { to: a.to } : {};
        return (
          <Wrapper
            key={i}
            {...linkProps}
            className={cn(
              'press inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs transition-colors duration-100 ease-out focus-ring',
              a.tone === 'danger'
                ? 'border-danger/30 bg-danger/5 hover:bg-danger/10'
                : 'border-warning/30 bg-warning/5 hover:bg-warning/10',
              a.to && 'cursor-pointer',
            )}
          >
            <Icon
              className={cn('h-3.5 w-3.5 shrink-0', a.tone === 'danger' ? 'text-danger' : 'text-warning')}
              aria-hidden
            />
            <div className="min-w-0">
              <p className="truncate font-medium">{a.label}</p>
              <p className="truncate text-[10.5px] text-muted-foreground">{a.detail}</p>
            </div>
            {a.to ? <ChevronRight className="h-3 w-3 opacity-50" aria-hidden /> : null}
          </Wrapper>
        );
      })}
    </div>
  );
}

interface SupplyStatProps {
  label: string;
  value: number | null;
  format: (n: number) => string;
  hint?: string;
  loading?: boolean;
  icon?: React.ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
}

function SupplyStat({ label, value, format, hint, loading, icon, tone = 'neutral' }: SupplyStatProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md',
            tone === 'success' && 'bg-success/10 text-success',
            tone === 'warning' && 'bg-warning/15 text-warning-soft',
            tone === 'danger' && 'bg-danger/10 text-danger',
            tone === 'neutral' && 'bg-muted text-muted-foreground',
          )}
        >
          {icon ?? <Boxes className="h-4 w-4" />}
        </span>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <p className="font-display text-2xl font-bold tracking-tight kpi-num sm:text-3xl">
            <AnimatedNumber value={value} format={format} duration={500} />
          </p>
        )}
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardBody>
    </Card>
  );
}

function KVRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'danger';
}) {
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('kpi-num font-semibold', tone === 'danger' && 'text-danger')}>
        <AnimatedNumber value={value} format={(n) => fmt.number(n)} duration={400} />
      </span>
    </li>
  );
}
